import React, { useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import Box from '@material-ui/core/Box';
import FormHelperText from '@material-ui/core/FormHelperText';

import { t } from 'locales/i18n';
import { useTranslation } from 'hooks/useTranslation';
import { Buttons } from 'shared';

import { CameFromOption } from 'features/auth/components/CameFromOption';
import BaseButton from 'features/app/components/BaseButton';
import { CameFromType } from 'features/auth/ts';

import { useStyles } from './styles';
import { useHistory } from 'react-router-dom';

const CAME_FROM_OPTIONS = [
  {
    value: CameFromType.A_FRIEND_OR_COLLEAGUE,
    label: t('cameFromOptions.friendOrColleague', { ns: 'auth' }),
    name: `came_from.${CameFromType.A_FRIEND_OR_COLLEAGUE}`,
  },
  {
    value: CameFromType.SOCIAL_MEDIA,
    label: t('cameFromOptions.socialMedia', { ns: 'auth' }),
    name: `came_from.${CameFromType.SOCIAL_MEDIA}`,
  },
  {
    value: CameFromType.SEARCH_ENGINE,
    label: t('cameFromOptions.searchEngine', { ns: 'auth' }),
    name: `came_from.${CameFromType.SEARCH_ENGINE}`,
  },
  {
    value: CameFromType.OTHER,
    label: t('cameFromOptions.other', { ns: 'auth' }),
    name: `came_from.${CameFromType.OTHER}`,
  },
];

type Props = {
  redirectToFirstStep: () => void;
  onSignUpFormSubmit: () => void;
};

export const CameFromForm: React.FC<Props> = ({ redirectToFirstStep, onSignUpFormSubmit }) => {
  const classes = useStyles();
  const { t: translate } = useTranslation('auth');
  const history = useHistory();

  const { watch } = useFormContext() ?? {};

  const cameFromOptionsValues = watch(CAME_FROM_OPTIONS.map(o => o.name));

  const errorMessage = useMemo(() => {
    const isNoOptionSelected = cameFromOptionsValues.every(v => v === false);

    return isNoOptionSelected ? t('selectAtLeastOneOption', { ns: 'auth' }) : null;
  }, [cameFromOptionsValues]);

  const onBackToFirstStep = useCallback(() => {
    redirectToFirstStep();
  }, [redirectToFirstStep]);

  const onSubmit = useCallback(() => {
    if (errorMessage) return;

    onSignUpFormSubmit();
  }, [errorMessage, onSignUpFormSubmit]);

  useEffect(() => {
    const locationHash = window.location.hash;
    window.history.pushState(null, '', '#second-step');
    return () => {
      window.location.hash = locationHash;
    };
  }, []);

  useEffect(() => {
    const historyListener = history.listen((_, action) => {
      if (action === 'POP') onBackToFirstStep();
    });
    return () => {
      historyListener();
    };
  }, [history, onBackToFirstStep]);

  return (
    <div>
      <Box className={classes.cameFromOptions}>
        {CAME_FROM_OPTIONS.map(option => (
          <CameFromOption {...option} key={option.value} />
        ))}
        {errorMessage && <FormHelperText className={classes.cameFromOptionsError}>{errorMessage}</FormHelperText>}
      </Box>

      <BaseButton
        className={classes.previousStepButton}
        labelClass={classes.previousStepButtonLabel}
        variant="outlined"
        borderRadius={5}
        fullWidth
        onClick={() => history.goBack()}
      >
        {translate('backToFirstStep')}
      </BaseButton>

      <Buttons.Auth
        title={translate('signUp')}
        fullWidth
        color="primary"
        variant="outlined"
        labelClass={classes.previousStepButtonLabel}
        onClick={onSubmit}
      />
    </div>
  );
};
